import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import interviewerImage from "../../../assets/icons/image/Interview-AI-image.png";
import { 
  getActiveInterview, 
  getAllActiveInterviews, 
  getCompletedInterviews,
  submitStudentSurvey,
} from "../../../application/services/studentDashboard";
import { 
  setActiveInterview, 
  setUpcomingInterviews, 
  setCompletedInterviews 
} from "../../../store/studentPageReducer";

const InterviewInterface = ({ interviewId, candidateName, studentId, interviewName, duration, onEndInterview }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.login.student);
  const email = user?.email || "";
  
  const [statusText, setStatusText] = useState("Initializing interview...");
  const [indicator, setIndicator] = useState("Listening");
  const [timeRemaining, setTimeRemaining] = useState(duration * 60);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState("Verifying identity...");
  const [isEndingInterview, setIsEndingInterview] = useState(false);
  const [showPoorConnection, setShowPoorConnection] = useState(false);
  const [reconnectBanner, setReconnectBanner] = useState({
    visible: false,
    message: "",
    isFatal: false,
  });
  const [showTabChangeAlert, setShowTabChangeAlert] = useState(false);
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(null);
  const [questionText, setQuestionText] = useState(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [showInterviewCompletedMessage, setShowInterviewCompletedMessage] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  
  const sessionIdRef = useRef(null);
  const previousNetworkTypeRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const disconnectionTimeoutRef = useRef(null);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const audioLevelIntervalRef = useRef(null);
  const playPromiseRef = useRef(null);
  const hasStartedRef = useRef(false);
  const isStartingRef = useRef(false);
  const progressMonitorRef = useRef(null);
  const interviewCompletedRef = useRef(false);

  const RECONNECT_TIMEOUT = 1 * 60 * 1000;

  const showReconnectBanner = (message, isFatal = false) => {
    setReconnectBanner({ visible: true, message, isFatal });
  };

  const hideReconnectBanner = () => {
    setReconnectBanner({ visible: false, message: "", isFatal: false });
  };

  const getApiBaseUrl = () => {
  const hostname = window.location.hostname;

  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("dev")
  )
    ? "https://testinterviewbotmeet.kraftshala.com/unified"
    : "https://interview.kraftshala.com/unified";
  };

  useEffect(() => {
    if (hasStartedRef.current || isStartingRef.current) {
      return;
    }
    
    isStartingRef.current = true;
    startInterview().finally(() => {
      isStartingRef.current = false;
    });
    
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading || isEndingInterview) {
      return;
    }

    const handleOnline = () => {
      if (!isLoading && !isEndingInterview && hasStartedRef.current) {
        setShowPoorConnection(false);
        hideReconnectBanner();
        setIndicator("Listening");
        if (disconnectionTimeoutRef.current) {
          clearTimeout(disconnectionTimeoutRef.current);
          disconnectionTimeoutRef.current = null;
        }
      }
    };

    const handleOffline = () => {
      if (!isLoading && !isEndingInterview && hasStartedRef.current) {
        setShowPoorConnection(true);
        setIndicator("Connection lost...");
        setStatusText("Poor connection detected. Attempting to reconnect...");
        showReconnectBanner("You're offline. Trying to reconnect…");
        if (disconnectionTimeoutRef.current) {
          clearTimeout(disconnectionTimeoutRef.current);
        }
        disconnectionTimeoutRef.current = setTimeout(() => {
          if (!navigator.onLine) {
            handleInterviewComplete();
          }
        }, 10000);
      }
    };

    const handleConnectionChange = () => {
      if (isLoading || isEndingInterview || !hasStartedRef.current) {
        return;
      }

      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (connection) {
        const effectiveType = connection.effectiveType;
        const type = connection.type;
        
        const currentNetworkInfo = `${effectiveType || type || 'unknown'}`;
        
        if (previousNetworkTypeRef.current && previousNetworkTypeRef.current !== currentNetworkInfo) {
          setShowPoorConnection(true);
        }
        
        if (effectiveType === '4g' || type === 'wifi' || type === 'ethernet') {
          setShowPoorConnection(false);
        }
        
        previousNetworkTypeRef.current = currentNetworkInfo;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
      
      const effectiveType = connection.effectiveType;
      const type = connection.type;
      const initialNetworkInfo = `${effectiveType || type || 'unknown'}`;
      previousNetworkTypeRef.current = initialNetworkInfo;
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isEndingInterview]);

  useEffect(() => {
    if (isLoading || isEndingInterview || !isInterviewActive) {
      setShowTabChangeAlert(false);
      return;
    }

    let wasVisible = !document.hidden;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        wasVisible = false;
        setShowTabChangeAlert(true);
      } else {
        if (!wasVisible) {
          setShowTabChangeAlert(true);
        }
        wasVisible = true;
      }
    };

    const handleBlur = () => {
      if (!document.hidden) {
        setShowTabChangeAlert(true);
      }
    };

    const handleFocus = () => {
      if (!document.hidden) {
        setTimeout(() => {
          if (!document.hidden) {
            setShowTabChangeAlert(false);
            wasVisible = true;
          }
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, true);
    window.addEventListener('blur', handleBlur, true);
    window.addEventListener('focus', handleFocus, true);
    window.addEventListener('beforeunload', (e) => {
      if (isInterviewActive) {
        e.preventDefault();
        e.returnValue = '';
      }
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
      window.removeEventListener('blur', handleBlur, true);
      window.removeEventListener('focus', handleFocus, true);
    };
  }, [isLoading, isEndingInterview, isInterviewActive]);

  const monitorProgress = () => {
    if (progressMonitorRef.current) {
      clearInterval(progressMonitorRef.current);
    }

    progressMonitorRef.current = setInterval(async () => {
      if (!sessionIdRef.current || isEndingInterview || interviewCompletedRef.current) {
        return;
      }

      try {
        const statusUrl = `${getApiBaseUrl()}/api/interview/status/${sessionIdRef.current}`;
        const response = await fetch(statusUrl);

        if (!response.ok) {
          return;
        }

        const data = await response.json();

        if (data && data.progress) {
          setStatusText(`Progress: ${data.progress}`);

          if (data.current_question !== undefined && data.total_questions !== undefined) {
            const percentage = (data.current_question / data.total_questions) * 100;
            setProgressPercentage(percentage);
            setCurrentQuestion(data.current_question);
            setTotalQuestions(data.total_questions);
          }

          if (data.question_text) {
            setQuestionText(data.question_text);
          }
        }
      } catch (error) {
        console.warn("⚠️ Status check failed:", error);
      }
    }, 3000);
  };

  const cleanup = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (audioLevelIntervalRef.current) {
      clearInterval(audioLevelIntervalRef.current);
      audioLevelIntervalRef.current = null;
    }
    if (progressMonitorRef.current) {
      clearInterval(progressMonitorRef.current);
      progressMonitorRef.current = null;
    }
    if (disconnectionTimeoutRef.current) {
      clearTimeout(disconnectionTimeoutRef.current);
      disconnectionTimeoutRef.current = null;
    }
    if (dataChannelRef.current) {
      try { dataChannelRef.current.close(); } catch (e) {}
      dataChannelRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (playPromiseRef.current) {
      playPromiseRef.current.catch(() => {});
      playPromiseRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    sessionIdRef.current = null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} remaining`;
  };

  const startTimer = () => {
    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startInterview = async () => {
    if (hasStartedRef.current && sessionIdRef.current) {
      console.log("Interview already started, skipping duplicate call");
      return;
    }
    
    hasStartedRef.current = true;
    setIsLoading(true);
    
    try {
      setLoadingStep("Verifying identity...");
      setStatusText("Verifying identity...");
      const verifyUrl = `${getApiBaseUrl()}/api/interview/verify`;

      const verifyResponse = await fetch(verifyUrl, {    
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          interview_id: interviewId,
          candidate_name: candidateName,
          student_id: studentId,
        }),
      });


      if (!verifyResponse.ok) {
        const errorText = await verifyResponse.text();
        console.error("❌ Verify API error:", errorText);
        setIsLoading(false);
        
        // Handle 502 Bad Gateway error - end the interview
        if (verifyResponse.status === 502) {
          setStatusText("Server error (502 Bad Gateway). Ending interview...");
          setIndicator("❌ Server Error");
          hasStartedRef.current = false;
          cleanup();
          setTimeout(() => {
            if (onEndInterview) {
              onEndInterview();
            }
          }, 1000);
          return;
        }
        
        throw new Error(
          `Server error (${verifyResponse.status}): ${verifyResponse.statusText}. ${errorText || "Please check if the API server is running."}`
        );
      }

      const verifyData = await verifyResponse.json();

      if (!verifyData.verified) {
        setIsLoading(false);
        setStatusText(`Verification failed: ${verifyData.message || "Unknown error"}`);
        return;
      }

      sessionIdRef.current = verifyData.session_token;

      setLoadingStep("Starting interview session...");
      setStatusText("Starting interview session...");
      const startUrl = `${getApiBaseUrl()}/api/interview/start`;
      
      const startResponse = await fetch(startUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_token: sessionIdRef.current }),
      });

      if (!startResponse.ok) {
        const errorText = await startResponse.text();
        setIsLoading(false);
        
        // Handle 502 Bad Gateway error - end the interview
        if (startResponse.status === 502) {
          setStatusText("Server error (502 Bad Gateway). Ending interview...");
          setIndicator("❌ Server Error");
          hasStartedRef.current = false;
          cleanup();
          setTimeout(() => {
            if (onEndInterview) {
              onEndInterview();
            }
          }, 1000);
          return;
        }
        
        throw new Error(
          `Failed to start interview (${startResponse.status}): ${startResponse.statusText}. ${errorText || "Please check if the API server is running."}`
        );
      }

      setLoadingStep("Setting up connection...");
      setStatusText("Please allow camera and microphone access...");
      setIndicator("Connecting...");

      await setupWebRTC();
      
      setIsLoading(false);
    } catch (error) {
      console.error("❌ Start interview error:", error);
      hasStartedRef.current = false;
      setIsLoading(false);
      
      // Check if error message contains 502 or Bad Gateway
      const is502Error = error.message?.includes("502") || error.message?.includes("Bad Gateway");
      
      if (is502Error) {
        setStatusText("Server error (502 Bad Gateway). Ending interview...");
        setIndicator("❌ Server Error");
        cleanup();
        setTimeout(() => {
          if (onEndInterview) {
            onEndInterview();
          }
        }, 1000);
        return;
      }
      
      let errorMessage = "Failed to start interview: ";
      
      if (error.message) {
        errorMessage += error.message;
      } else if (error.name === "TypeError" && error.message.includes("fetch")) {
        errorMessage += "Network error. Please check your internet connection and API server status.";
      } else {
        errorMessage += "Unknown error occurred. Please try again.";
      }
      
      setStatusText(errorMessage);
      setIndicator("❌ Connection Failed");
    }
  };

  const setupWebRTC = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Microphone API not available. Please use HTTPS or access via http://localhost:7860");
      }

      localStreamRef.current = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Enable video track
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = true;
        setIsVideoOn(true);
      }

      // Enable audio track (microphone)
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = true;
        setIsMuted(false);
      }

      if (localVideoRef.current && localStreamRef.current) {
        if (playPromiseRef.current) {
          playPromiseRef.current.catch(() => {});
          playPromiseRef.current = null;
        }
        
        if (localVideoRef.current.srcObject) {
          localVideoRef.current.srcObject = null;
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        localVideoRef.current.srcObject = localStreamRef.current;
        localVideoRef.current.muted = true;
        
        try {
          const playPromise = localVideoRef.current.play();
          playPromiseRef.current = playPromise;
          
          if (playPromise !== undefined) {
            await playPromise;
          }
          playPromiseRef.current = null;
          console.log("Video stream connected and playing successfully");
        } catch (playError) {
          if (playError.name !== 'AbortError' && playError.name !== 'NotAllowedError') {
            console.warn("Video play interrupted:", playError);
          }
          playPromiseRef.current = null;
        }
      }

      setStatusText("Connecting to AI interviewer...");
      setIndicator("Connecting...");

      const apiBaseUrl = getApiBaseUrl();
      const turnDomain = new URL(apiBaseUrl).hostname;

      peerConnectionRef.current = new RTCPeerConnection({
        iceServers: [
          {
            urls: `turn:${turnDomain}:3478?transport=udp`,
            username: "turnuser",
            credential: "turnpassword",
          },
          {
            urls: `turn:${turnDomain}:3478?transport=tcp`,
            username: "turnuser",
            credential: "turnpassword",
          },
          {
            urls: `turns:${turnDomain}:5349`,
            username: "turnuser",
            credential: "turnpassword",
          },
        ],
      });

      localStreamRef.current.getTracks().forEach((track) => {
        peerConnectionRef.current.addTrack(track, localStreamRef.current);
      });

      peerConnectionRef.current.ontrack = (event) => {
        const audio = new Audio();
        audio.srcObject = event.streams[0];
        audio.play().catch((err) => {
          console.error("Failed to play AI audio:", err);
        });
      };

      peerConnectionRef.current.ondatachannel = (event) => {
        const receiveChannel = event.channel;
        console.log("📡 Data channel received:", receiveChannel.label);

        receiveChannel.onopen = () => {
          console.log("✅ Data channel is open:", receiveChannel.label);
        };
        receiveChannel.onclose = () => {
          console.log("❌ Data channel closed:", receiveChannel.label);
        };
        receiveChannel.onerror = (err) => {
          console.error("⚠️ Data channel error:", err);
        };
        receiveChannel.onmessage = (e) => {
          console.log("📩 Received data:", e.data);
        };
      };

      peerConnectionRef.current.onconnectionstatechange = () => {
        const connectionState = peerConnectionRef.current?.connectionState;
        
        if (connectionState === "connected") {
          if (disconnectionTimeoutRef.current) {
            clearTimeout(disconnectionTimeoutRef.current);
            disconnectionTimeoutRef.current = null;
          }
          setShowPoorConnection(false);
          hideReconnectBanner();
          setIndicator("Listening");
          setIsInterviewActive(true);
          if (!timerIntervalRef.current) {
            startTimer();
            setStatusText("✅ Interview started!");
          }
          if (progressMonitorRef.current) {
            clearInterval(progressMonitorRef.current);
            progressMonitorRef.current = null;
          }
          monitorProgress();
        } else if (
          connectionState === "disconnected" ||
          connectionState === "failed"
        ) {
          if (disconnectionTimeoutRef.current) {
            clearTimeout(disconnectionTimeoutRef.current);
          }
  
          disconnectionTimeoutRef.current = setTimeout(async () => {
          if (interviewCompletedRef.current) return;
          const stateNow = peerConnectionRef.current?.connectionState;
          if (stateNow === "connected") return;

            try {
              const statusUrl = `${getApiBaseUrl()}/api/interview/status/${sessionIdRef.current}`;
              const response = await fetch(statusUrl);
              if (response.ok) {
                const data = await response.json();
                if (data.status === "completed" || data.status === "ending") {
                  handleInterviewComplete();
                  return;
                }
              }
            } catch (e) {
              // ignore, fall through to reconnect UI
            }
          setShowPoorConnection(true);
          setIndicator("Reconnecting...");
          setStatusText("Connection interrupted. Attempting to reconnect...");
          showReconnectBanner("Connection interrupted — reconnecting…");
          setIsInterviewActive(false);

          disconnectionTimeoutRef.current = setTimeout(() => {
            const stateNow = peerConnectionRef.current?.connectionState;
            if (stateNow !== "connected" && !interviewCompletedRef.current) {
              showReconnectBanner(
                "Couldn't restore connection. Ending the interview…",
                true
              );
              setTimeout(() =>{ 
                if( !interviewCompletedRef.current){
                   handleInterviewComplete();
                }
              }, 1200);
            }
          }, RECONNECT_TIMEOUT);
          },2000);
        } else if (connectionState === "closed") {
          if (disconnectionTimeoutRef.current) {
            clearTimeout(disconnectionTimeoutRef.current);
            disconnectionTimeoutRef.current = null;
          }
          handleInterviewComplete();
        }
      };

      try {
        const dataChannel = peerConnectionRef.current.createDataChannel("interview", {
          ordered: true,
        });
        dataChannelRef.current = dataChannel;

        dataChannel.onopen = () => {
          console.log("✅ Client data channel is open:", dataChannel.label);
        };
        dataChannel.onclose = () => {
          console.log("❌ Client data channel closed:", dataChannel.label);
        };
        dataChannel.onerror = (err) => {
          console.error("⚠️ Client data channel error:", err);
        };
        dataChannel.onmessage = (e) => {
          console.log("📩 Received on client data channel:", e.data);
        };
      } catch (dcError) {
        console.warn("⚠️ Failed to create data channel (continuing without it):", dcError);
      }

      const offer = await peerConnectionRef.current.createOffer({
        offerToReceiveAudio: true,
      });
      await peerConnectionRef.current.setLocalDescription(offer);

      setLoadingStep("Establishing connection...");
      setStatusText("Establishing connection...");
      
      const offerUrl = `${getApiBaseUrl()}/offer`;
      const response = await fetch(offerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sdp: offer.sdp,
          type: offer.type,
          session_id: sessionIdRef.current,
        }),
      });

      // Handle 502 Bad Gateway error
      if (!response.ok && response.status === 502) {
        console.error("❌ 502 Bad Gateway error on offer endpoint - ending interview");
        setIsLoading(false);
        setStatusText("Server error (502 Bad Gateway). Ending interview...");
        setIndicator("❌ Server Error");
        cleanup();
        setTimeout(() => {
          if (onEndInterview) {
            onEndInterview();
          }
        }, 1000);
        return;
      }

      const answer = await response.json();
      if (answer.error) {
        setIsLoading(false);
        throw new Error(answer.error);
      }

      await peerConnectionRef.current.setRemoteDescription(
        new RTCSessionDescription({
          sdp: answer.sdp,
          type: answer.type,
        })
      );
    } catch (error) {
      console.error("WebRTC setup failed:", error);
      setIsLoading(false);
      
      // Check if error message contains 502 or Bad Gateway
      const is502Error = error.message?.includes("502") || error.message?.includes("Bad Gateway");
      
      if (is502Error) {
        console.error("❌ 502 Bad Gateway error detected in WebRTC setup - ending interview");
        setStatusText("Server error (502 Bad Gateway). Ending interview...");
        setIndicator("❌ Server Error");
        cleanup();
        setTimeout(() => {
          if (onEndInterview) {
            onEndInterview();
          }
        }, 1000);
        return;
      }
      
      let errorMessage = "Failed to setup camera and microphone connection";
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        errorMessage = "Camera/Microphone API not available. Please access via http://localhost:7860 or use HTTPS.";
      } else if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        errorMessage = "Camera/Microphone permission denied. Please allow camera and microphone access and try again.";
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        errorMessage = "No camera or microphone found. Please connect a camera and microphone and try again.";
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        errorMessage = "Camera or microphone is already in use by another application.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      setStatusText(errorMessage);
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        const newMutedState = !isMuted;
        audioTrack.enabled = !newMutedState; // enabled = true means unmuted, false means muted
        setIsMuted(newMutedState);
        
        // Also update RTCRtpSender if track is being sent via WebRTC
        if (peerConnectionRef.current) {
          const senders = peerConnectionRef.current.getSenders();
          const audioSender = senders.find(sender => 
            sender.track && sender.track.kind === 'audio'
          );
          if (audioSender && audioSender.track) {
            audioSender.track.enabled = !newMutedState;
          }
        }
        
        console.log(`Microphone ${newMutedState ? 'muted' : 'unmuted'}`);
      }
    }
  };

  const handleInterviewComplete = async () => {
    if (interviewCompletedRef.current) return;
    interviewCompletedRef.current = true;
    setIsInterviewActive(false);
    setShowTabChangeAlert(false);
    setShowPoorConnection(false);
    hideReconnectBanner();
    if (disconnectionTimeoutRef.current) {
      clearTimeout(disconnectionTimeoutRef.current);
      disconnectionTimeoutRef.current = null;
    }
    cleanup();
    setShowFeedbackForm(true);
  };

  const finishAndExit = () => {
    setShowFeedbackForm(false);
    setShowInterviewCompletedMessage(true);

    if (email) {
      Promise.allSettled([
        getActiveInterview(email),
        getAllActiveInterviews(email, 1, 6),
        getCompletedInterviews(email, 1, 5),
      ]).then(([active, upcoming, completed]) => {
        if (active.status === "fulfilled") {
          try {
            dispatch(setActiveInterview(active.value?.data || null));
          } catch (error) {
            console.error("Error updating active interview:", error);
          }
        }
        if (upcoming.status === "fulfilled") {
          try {
            const upcomingData = upcoming.value?.data?.data || upcoming.value?.data || [];
            dispatch(setUpcomingInterviews(upcomingData));
          } catch (error) {
            console.error("Error updating upcoming interviews:", error);
          }
        }
        if (completed.status === "fulfilled") {
          try {
            const completedData = completed.value?.data?.data || completed.value?.data || [];
            dispatch(setCompletedInterviews(completedData));
          } catch (error) {
            console.error("Error updating completed interviews:", error);
          }
        }
      }).catch((error) => {
        console.error("Error refetching interview data:", error);
      });
    }

    setTimeout(() => {
      if (onEndInterview) onEndInterview();
    }, 2000);
  };

  const handleFeedbackSubmit = async () => {
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await submitStudentSurvey({
        interview_id: interviewId,
        rating: feedbackRating || 0,
        comment: feedbackComment?.trim() || "",
      });
      finishAndExit();
    } catch (err) {
      setFeedbackError(err?.message || "Failed to submit feedback.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const handleFeedbackSkip = () => {
    finishAndExit();
  };

  const handleEndInterview = async () => {
    if (interviewCompletedRef.current) return;
    interviewCompletedRef.current = true;
    setIsInterviewActive(false);
    setShowTabChangeAlert(false);
    setShowPoorConnection(false);
    hideReconnectBanner();
    setIsEndingInterview(true);
    setStatusText("Ending interview...");
    
    try {
      const endUrl = `${getApiBaseUrl()}/api/interview/end`;
      try {
        const endResponse = await fetch(endUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionIdRef.current }),
        });

        if (!endResponse.ok) {
          const errorText = await endResponse.text();
          console.warn("⚠️ End API returned error:", endResponse.status, errorText);
        } else {
          const endData = await endResponse.json();
          
          if (endData.status === "completed" && email) {
            Promise.allSettled([
              getActiveInterview(email),
              getAllActiveInterviews(email, 1, 6),
              getCompletedInterviews(email, 1, 5),
            ]).then(([active, upcoming, completed]) => {
              if (active.status === "fulfilled") {
                try {
                  dispatch(setActiveInterview(active.value?.data || null));
                } catch (error) {
                  console.error("Error updating active interview:", error);
                }
              }
              
              if (upcoming.status === "fulfilled") {
                try {
                  const upcomingData = upcoming.value?.data?.data || upcoming.value?.data || [];
                  dispatch(setUpcomingInterviews(upcomingData));
                } catch (error) {
                  console.error("Error updating upcoming interviews:", error);
                }
              }
              
              if (completed.status === "fulfilled") {
                try {
                  const completedData = completed.value?.data?.data || completed.value?.data || [];
                  dispatch(setCompletedInterviews(completedData));
                } catch (error) {
                  console.error("Error updating completed interviews:", error);
                }
              }
            }).catch((error) => {
              console.error("Error refetching interview data:", error);
            });
          }
        }
      } catch (corsError) {
        if (corsError.name === "TypeError" && corsError.message.includes("fetch")) {
          console.log("🔄 CORS error detected, retrying with no-cors mode...");
          await fetch(endUrl, {
            method: "POST",
            mode: "no-cors",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionIdRef.current }),
          });
          console.log("📡 End API called (no-cors mode - request sent to server)");
        } else {
          throw corsError;
        }
      }
    } catch (error) {
      console.warn("⚠️ Failed to call end endpoint (non-critical):", error.message);
    }

    cleanup();
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setIsEndingInterview(false);
    setShowFeedbackForm(true);
  };

  const interviewInterface = (
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 bg-gray-900 flex flex-col z-[9999]" 
      style={{ 
        position: 'fixed',
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0,
        zIndex: 9999,
        backgroundColor: '#111827'
      }}
    >
      {isLoading && !isEndingInterview && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-[10000]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-400 mb-4"></div>
            <h2 className="text-white text-xl font-semibold mb-2">{loadingStep}</h2>
            <p className="text-gray-400 text-sm">{statusText}</p>
          </div>
        </div>
      )}

      {isEndingInterview && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-[10000]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-400 mb-4"></div>
            <h2 className="text-white text-xl font-semibold mb-2">Ending Interview</h2>
            <p className="text-gray-400 text-sm">Please wait while we finalize your interview session...</p>
          </div>
        </div>
      )}

      {showPoorConnection && !isLoading && !isEndingInterview && !showInterviewCompletedMessage && (
        <div className="bg-red-600 text-white px-6 py-3 flex items-center justify-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
          <span className="font-medium">Poor Connection</span>
        </div>
      )}

      {reconnectBanner.visible && !isLoading && !isEndingInterview && !showInterviewCompletedMessage && (
        <div
          className={`px-6 py-3 flex items-center justify-center gap-2 ${
            reconnectBanner.isFatal ? "bg-gray-800 text-red-200" : "bg-yellow-500 text-gray-900"
          }`}
        >
          {!reconnectBanner.isFatal ? (
            <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-gray-900 border-t-transparent" />
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          <span className="font-medium">{reconnectBanner.message}</span>
        </div>
      )}

      {showFeedbackForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[10002]"
          style={{ zIndex: 10002 }}
        >
          <div className="bg-gray-800 rounded-xl p-8 max-w-md mx-4 w-full shadow-2xl border border-gray-600">
            <h2 className="text-xl font-bold text-white mb-2">Share your feedback</h2>
            <p className="text-gray-400 text-sm mb-6">How was your interview experience?</p>
            {feedbackError && (
              <p className="text-red-400 text-sm mb-3">{feedbackError}</p>
            )}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Rating</label>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFeedbackRating(star)}
                    className="focus:outline-none"
                    aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  >
                    <span className={`text-3xl ${feedbackRating >= star ? "text-yellow-400" : "text-gray-500"}`}>
                      ★
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-1">{feedbackRating ? `${feedbackRating} of 5` : "Select a rating"}</p>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Comment (optional)</label>
              <textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Any additional feedback..."
                rows={3}
                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 outline-none"
              />
            </div>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={handleFeedbackSubmit}
                disabled={feedbackSubmitting}
                className="px-6 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {feedbackSubmitting ? "Submitting…" : "Submit"}
              </button>
              <button
                type="button"
                onClick={handleFeedbackSkip}
                disabled={feedbackSubmitting}
                className="px-6 py-2.5 bg-gray-600 hover:bg-gray-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {showInterviewCompletedMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[10002]"
          style={{ zIndex: 10002 }}
        >
          <div className="bg-gray-800 rounded-xl p-8 max-w-md mx-4 text-center shadow-2xl border border-gray-600">
            <div className="mb-6">
              <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-6">Thank you for the interview!! Good luck</h2>
          </div>
        </div>
      )}

      {showTabChangeAlert && !isLoading && !isEndingInterview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[10001]"
          style={{ zIndex: 10001 }}
        >
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="mb-4">
              <svg className="w-16 h-16 text-yellow-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Stay on This Tab</h2>
            <p className="text-gray-600 mb-6">
              Please return to this tab to continue your interview. Switching tabs or minimizing the window may affect your interview session.
            </p>
            <button
              onClick={() => {
                setShowTabChangeAlert(false);
                // Also clear the hidden state to allow re-detection
                if (!document.hidden) {
                  setShowTabChangeAlert(false);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              I Understand
            </button>
          </div>
        </div>
      )}

      {!isEndingInterview && (
        <div className="bg-gray-900 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="md:flex items-center gap-4">
            <h1 className="text-white text-lg font-semibold">{interviewName || "Interview"}</h1>
            {!isLoading && (
              <div className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                LIVE INTERVIEW
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            {!isLoading && (
              <>
                <span className="text-white text-sm">{formatTime(timeRemaining)}</span>
                <button
                  onClick={handleEndInterview}
                  disabled={isEndingInterview}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  End Interview
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {!isEndingInterview && (
        <div className="flex-1 relative bg-gray-900 overflow-hidden">
          <div className="absolute md:top-8 md:left-8 left-4 top-5 z-10 flex items-center gap-2">
            <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
            <span className="text-white text-sm font-medium">Recording</span>
          </div>

          <div className="absolute inset-0">
            <div className="w-full h-full flex flex-col lg:flex-row">
              {/* Video Section */}
              <div className="relative p-3 lg:p-4 h-[45vh] sm:h-[55vh] lg:h-auto flex-1">
                <div className="w-full h-full overflow-hidden rounded-xl bg-black shadow-2xl relative">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />

                  {!isVideoOn && (
                    <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                      <div className="w-24 h-24 lg:w-32 lg:h-32 bg-gray-700 rounded-full flex items-center justify-center">
                        <span className="text-white text-3xl lg:text-4xl">
                          {candidateName?.charAt(0)?.toUpperCase() || "U"}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Candidate Name */}
                  <div className="absolute bottom-4 left-4 lg:bottom-4 lg:left-8 text-white">
                    <div className="font-semibold text-base lg:text-lg">
                      {candidateName || "You"}
                    </div>
                    <div className="text-xs lg:text-sm text-gray-300">You</div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 bg-opacity-90 px-6 py-4">
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={toggleMute}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                          isMuted
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-gray-700 hover:bg-gray-600"
                        }`}
                      >
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {isMuted ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Sidebar */}
              <div className="w-full lg:w-96 bg-gray-900 flex flex-col lg:border-l border-t lg:border-t-0 border-gray-700">
                {/* Progress */}
                <div className="p-5 border-b border-gray-700">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-white font-medium">Progress</span>

                    <span className="text-gray-300 text-sm">
                      {currentQuestion}/{totalQuestions}
                    </span>
                  </div>

                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                </div>

                {/* AI Interviewer */}
                <div className="p-3">
                  <div className="bg-gray-800 rounded-xl border border-gray-700 p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={interviewerImage}
                          alt="AI Interviewer"
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div>
                        <div className="text-white font-semibold">
                          AI Interviewer
                        </div>

                        <div className="text-xs lg:text-sm text-gray-400 mt-1">
                          {indicator}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="flex-1 min-h-0 p-5 overflow-y-auto px-4 lg:px-5 py-4 lg:py-5 max-h-[35vh] lg:max-h-none">
                  <h3 className="text-yellow-400 text-sm font-semibold uppercase tracking-wide mb-3">
                    Current Question
                  </h3>

                  {questionText ? (
                    <div className="text-white text-sm lg:text-base leading-6 lg:leading-7">
                      {questionText}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">
                      Waiting for the question...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          
        </div>
      )}
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(interviewInterface, document.body) : interviewInterface;
};

export default InterviewInterface;

